import { useState, useEffect } from 'react'
import { clsx } from 'clsx'

export interface CarouselSlide {
  id: string
  headline: string
  subheadline?: string
  backgroundImage: string
  backgroundColor?: string
}

interface CarouselSliderProps {
  slides: CarouselSlide[]
  autoPlayInterval?: number
  height?: string
  className?: string
  indicatorClassName?: string
}

export default function CarouselSlider({
  slides,
  autoPlayInterval = 5200,
  height = 'h-full',
  className = '',
  indicatorClassName = '',
}: CarouselSliderProps) {
  const [activeSlide, setActiveSlide] = useState(0)
  const indicatorPositionClass = indicatorClassName || 'left-6 top-6 sm:left-8 sm:top-8'

  useEffect(() => {
    if (slides.length <= 1) return

    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length)
    }, autoPlayInterval)

    return () => clearInterval(timer)
  }, [slides.length, autoPlayInterval])

  if (slides.length === 0) return null

  return (
    <div className={clsx('relative overflow-hidden border', height, className)}>
      <div className="relative h-full w-full">
        {slides.map((slide, idx) => (
          <div
            key={slide.id}
            className={clsx(
              'absolute inset-0 transition-opacity duration-[1400ms] ease-out',
              idx === activeSlide ? 'opacity-100' : 'opacity-0'
            )}
          >
            <img
              src={slide.backgroundImage}
              alt=""
              className={clsx(
                'absolute inset-0 h-full w-full object-cover transition-transform duration-[5200ms] ease-out',
                idx === activeSlide ? 'scale-[1.06]' : 'scale-100'
              )}
            />

            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(9,14,12,0.16)_0%,rgba(9,14,12,0.32)_34%,rgba(9,14,12,0.72)_100%)]" />

            <div className="relative z-10 flex h-full flex-col justify-end p-6 sm:p-8 text-white">
              <div
                className={clsx(
                  'mt-auto max-w-sm transition-all duration-[900ms] ease-out',
                  idx === activeSlide ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                )}
              >
                <h3 className="text-[28px] font-bold leading-[1.08] sm:text-[34px] lg:text-[38px]">
                  {slide.headline}
                </h3>
                {slide.subheadline && (
                  <p className="mt-3 max-w-xs text-sm leading-6 text-white/82 sm:text-[15px]">
                    {slide.subheadline}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={clsx('absolute z-20 flex gap-2', indicatorPositionClass)}>
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setActiveSlide(idx)}
            className={clsx(
              'h-1.5 rounded-full transition-all duration-500',
              idx === activeSlide
                ? 'w-9 bg-white'
                : 'w-3 bg-white/38 hover:bg-white/60'
            )}
            aria-label={`Go to slide ${idx + 1}`}
            type="button"
          />
        ))}
      </div>
    </div>
  )
}
